import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api from "../api";
import { setActiveProject, setRefreshProjects } from "../redux/projectSlice";
import { setRefreshSidebar } from "../redux/sidebarSlice";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import CreateProjectModal from "../components/CreateProjectModal";
import "../css/ProjectsPage.css";
import { Loader2 } from "lucide-react";

const ProjectsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const refreshProjects = useSelector((state) => state.project.refreshProjects);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openProjectMenuId, setOpenProjectMenuId] = useState(null);
  const [sortBy, setSortBy] = useState("updated");

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      const res = await api.get("/projects", { params });
      setProjects(res.data?.projects || []);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (refreshProjects) {
      fetchProjects();
      dispatch(setRefreshProjects(false));
    }
  }, [refreshProjects, fetchProjects, dispatch]);

  useEffect(() => {
    if (location.state?.openCreateModal) {
      setShowCreateModal(true);
      setEditingProject(null);
      navigate("/user/projects", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".project-card-menu-wrapper")) {
        setOpenProjectMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchProjects();
    }
  };

  const handleProjectClick = (project) => {
    dispatch(setActiveProject(project));
    navigate(`/user/projects/${project.project_id}`);
  };

  const handleCreateProject = () => {
    setEditingProject(null);
    setShowCreateModal(true);
  };

  const handleEditProject = (e, project) => {
    e.stopPropagation();
    setOpenProjectMenuId(null);
    setEditingProject(project);
    setShowCreateModal(true);
  };

  const handleDeleteClick = (e, project) => {
    e.stopPropagation();
    setOpenProjectMenuId(null);
    setDeleteTarget(project);
    setShowDeleteModal(true);
  };

  const handleTogglePin = async (e, project) => {
    e.stopPropagation();
    setOpenProjectMenuId(null);
    try {
      const res = await api.put(`/projects/${project.project_id}/pin`);
      const newPinned = res.data?.is_pinned;
      setProjects((prev) =>
        prev.map((p) =>
          p.project_id === project.project_id
            ? { ...p, is_pinned: newPinned }
            : p,
        ),
      );
      dispatch(setRefreshSidebar(true));
      showSuccessToast(newPinned ? "Project pinned" : "Project unpinned");
    } catch (err) {
      showErrorToast("Failed to update pin status");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${deleteTarget.project_id}`);
      showSuccessToast("Project deleted");
      setProjects((prev) =>
        prev.filter((p) => p.project_id !== deleteTarget.project_id),
      );
      dispatch(setRefreshSidebar(true));
    } catch (err) {
      showErrorToast("Failed to delete project");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleModalClose = (saved) => {
    setShowCreateModal(false);
    setEditingProject(null);
    if (saved) {
      fetchProjects();
      dispatch(setRefreshSidebar(true));
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const diffMs = date.getTime() - Date.now();
    const diffMinutes = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);

    if (Math.abs(diffMinutes) < 60) {
      return diffMinutes >= 0
        ? `in ${Math.max(1, diffMinutes)} min`
        : `${Math.max(1, Math.abs(diffMinutes))} min ago`;
    }
    if (Math.abs(diffHours) < 24) {
      return diffHours >= 0
        ? `in ${Math.max(1, diffHours)} hr`
        : `${Math.max(1, Math.abs(diffHours))} hr ago`;
    }
    return diffDays >= 0
      ? `in ${Math.max(1, diffDays)} days`
      : `${Math.max(1, Math.abs(diffDays))} days ago`;
  };

  const sortProjects = (list) => {
    const pinned = list.filter((p) => p.is_pinned);
    const unpinned = list.filter((p) => !p.is_pinned);

    const sortFn = (a, b) => {
      if (sortBy === "created") {
        return (
          new Date(b.created_timestamp || 0) -
          new Date(a.created_timestamp || 0)
        );
      }
      if (sortBy === "alphabetical") {
        return (a.name || "").localeCompare(b.name || "");
      }
      // Default: last updated
      return (
        new Date(b.updated_timestamp || 0) - new Date(a.updated_timestamp || 0)
      );
    };

    pinned.sort(sortFn);
    unpinned.sort(sortFn);
    return [...pinned, ...unpinned];
  };

  const myProjects = sortProjects(projects.filter((p) => p.is_owner));
  const sharedProjects = sortProjects(projects.filter((p) => !p.is_owner));

  console.log(
    "ProjectsPage render: myProjects:",
    myProjects,
    "sharedProjects:",
    sharedProjects,
  );

  return (
    <div className="projects-page custom-ak-scollbar overflow-hidden px-3">
      <div className="projects-header px-3 py-2 ak-text-sm">
        {/* <h1 className="projects-title ak-text-lg bold">Projects</h1> */}
        <h1 className="projects-title ak-text-lg bold"> </h1>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="projects-searchbar">
            <i
              className="bi bi-search search-icon"
              style={{
                width: "13px",
                height: "13px",
                objectFit: "contain",
              }}
            />
            <input
              placeholder="Search projects..."
              value={search}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              // className="projects-search-input"
              className="repo-search-input ak-text-sm border"
              style={{ fontSize: "ak-text-sm" }}
            />
          </div>
          <div className="projects-sort-dropdown ">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="updated">Last updated</option>
              <option value="created">Date created</option>
              <option value="alphabetical">Alphabetical</option>
            </select>
          </div>
          <button
            className="repo-btn fontimp repo-btn-primary repo-upload-inline-btn text-nowrap"
            onClick={handleCreateProject}
            style={{ fontSize: "13px" }}
          >
            <i className="bi bi-plus-lg me-2" />
            Create Project
          </button>
        </div>
      </div>

      {loading ? (
        // <div className="projects-loading m-2 gap-2 ">
        <div className="repo-loader-container">
          {/* <div className="spinner-border bg-light" role="status" /> */}
          <Loader2 className="animate-spin" size={32} />
          <p className="mt-2 ">Loading Projects...</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="projects-empty m-2">
          <div className="empty-icon" onClick={handleCreateProject}>
            <i className="bi bi-plus" />
          </div>
          <h3>No projects yet</h3>
          <p>
            Create a project to get started building your <br /> workspace.
          </p>
        </div>
      ) : (
        <div className="projects-sections py-1 custom-ak-scollbar overflow-auto mt-4">
          {myProjects.length > 0 && (
            <div className="project-section px-2">
              {/* <h3 className="section-title">My Projects</h3> */}
              <div className="projects-grid">
                {myProjects.map((project) => (
                  <div
                    key={project.project_id}
                    className="project-card"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="project-card-header">
                      <h4 className="project-card-name">
                        {project.name}{" "}
                        {project.is_pinned && (
                          <i
                            className="bi bi-pin-angle-fill me-1"
                            style={{
                              fontSize: "0.75rem",
                              display: "inline-block",
                            }}
                          />
                        )}
                      </h4>
                      <div className="project-card-menu-wrapper">
                        <button
                          className="project-card-menu-button"
                          title="Actions"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenProjectMenuId(
                              openProjectMenuId === project.project_id
                                ? null
                                : project.project_id,
                            );
                          }}
                          aria-label="Project actions"
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>
                        {openProjectMenuId === project.project_id && (
                          <div className="project-card-menu-popover">
                            <button
                              type="button"
                              onClick={(e) => handleEditProject(e, project)}
                            >
                              <i className="bi bi-pencil me-2" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleTogglePin(e, project)}
                            >
                              <i className="bi bi-pin-angle me-2" />
                              {project.is_pinned ? "Unpin" : "Pin"}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteClick(e, project)}
                              className="danger"
                            >
                              <i className="bi bi-trash me-2" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="project-card-body">
                      {project.description && (
                        <p className="project-card-desc">
                          {project.description}
                        </p>
                      )}
                    </div>
                    <div className="project-card-footer">
                      <span className="project-chat-count">
                        <i className="bi bi-chat-dots me-1" />
                        {project.chat_count}{" "}
                        {project.chat_count === 1 ? "chat" : "chats"}
                      </span>
                      <span className="project-date">
                        {formatRelativeTime(project.created_timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sharedProjects.length > 0 && (
            <div className="project-section px-2">
              <h3 className="section-title1 ak-text-lg">Shared with me</h3>
              <div className="projects-grid">
                {sharedProjects.map((project) => (
                  <div
                    key={project.project_id}
                    className="project-card shared"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="project-card-body">
                      <h4 className="project-card-name">{project.name}</h4>
                      {project.description && (
                        <p className="project-card-desc">
                          {project.description}
                        </p>
                      )}
                    </div>
                    <div className="project-card-footer">
                      <span className="project-chat-count">
                        <i className="bi bi-chat-dots me-1" />
                        {project.chat_count}{" "}
                        {project.chat_count === 1 ? "chat" : "chats"}
                      </span>
                      <span className="project-date">
                        {formatRelativeTime(project.created_timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateProjectModal
          project={editingProject}
          onClose={handleModalClose}
        />
      )}

      {showDeleteModal && deleteTarget && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div
            // className="modal-dialog modal-dialog-centered"
            className="m-auto d-flex flex-1"
            style={{ minHeight: "100vh" }}
          >
            <div
              className="modal-content"
              style={{ maxWidth: "400px", width: "100%" }}
            >
              <div className="modal-header">
                <h5 className="modal-title ak-text-lg">Delete Project</h5>
                <button
                  type="button"
                  className="btn-close border border-dark rounded-circle ak-text-sm"
                  onClick={() => setShowDeleteModal(false)}
                />
              </div>
              <div className="modal-body">
                <p className="m-0">
                  Are you sure you want to delete{" "}
                  <strong>{deleteTarget.name}</strong>?<br /> Chats in this
                  project will be unlinked but not deleted.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="cancle-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="custom-btn delete-btn"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
